import { ScreenType } from '@accedo/one-plugin-sdk';
import { FC, useCallback, useEffect } from 'react';
import { Link } from 'react-router';

import { userService } from '#/client/api';
import {
  CoreResponseType,
  LoginRequest,
  LoginRequestValues,
  SubmitResponse,
  TFormData,
} from '#/client/models';
import { ApiError } from '#/errors/serviceErrors';
import { useAutoLogin, useSubmitAutoLogin } from '#/hooks/useAutoLogin';
import { FormInputVariants, StorageKeys } from '#/service/constants';
import { Path } from '#/service/routes';
import { LS } from '#/service/storage';
import { getDeviceInfo } from '#/service/user/userSessionManager';
import { noop, techSearchParams } from '#/service/utils';
import { authActions } from '#/store/auth/auth.store';
import { showPersonalGreeting } from '#/store/snackbar/snackbar.utils';
import { tb } from '#/store/translation/translation.store';

import { Form } from '../Form/Form';
import { TInputState } from '../Form/Form.types';
import { createBasicFormConfig } from '../Form/Form.utils';
import { FormInput } from '../Form/FormInput/FormInput';
import { Spinner } from '../Spinner/Spinner';
import { useLoginStandard } from './LoginStandard.hook';
import {
  Container,
  ExtraOptions,
  RegistrationSection,
  SpinnerContainer,
} from './LoginStandard.styled';

interface Props {
  onSuccess: () => void;
  onCancel?: () => void;
  hideRememberMe?: boolean;
  hideRegister?: boolean;
  hideForgotPassword?: boolean;
  pageHeading: string;
  /** (Optional) Second Screen Auth Code that should be sent alongside the log in request */
  authCode?: string;
}

/** Login form for a standard (email + password) authentication flow */
export const LoginStandard: FC<Props> = ({
  onSuccess,
  onCancel,
  hideRememberMe = false,
  hideRegister = false,
  hideForgotPassword = false,
  pageHeading,
  authCode,
}) => {
  const { display, emailField, error, loading, rememberMe, setRememberMe } =
    useLoginStandard();
  const { isAutoLogin } = useAutoLogin();

  const submitForm = useCallback(
    async (data: TFormData): Promise<SubmitResponse> => {
      const res = await performLogin(data, pageHeading, authCode);

      const { type } = res?._meta ?? {};

      if (type === CoreResponseType.SUCCESS) {
        if (emailField) {
          if (rememberMe.value) {
            const emailData = data[emailField.key] as string;
            LS.set(StorageKeys.LOGIN_EMAIL, emailData);
            emailField.defaultValue = emailData;
          } else {
            LS.remove(StorageKeys.LOGIN_EMAIL);
            emailField.defaultValue = undefined;
          }
        }

        onSuccess();
      }

      return res;
    },
    [authCode, emailField, onSuccess, rememberMe.value, pageHeading]
  );

  const registerText = tb('web.login.registerText');
  const registerLink = tb('web.login.registerLink');
  const forgotPasswordLink = tb('login.forgotPasswordLink');

  if (error) {
    // ONEWEB-371 - config flag is required to enable/disable auth.
    // if auth disabled, none of this should run
    // for now, comment out error so it doesn't interfere with apps not using auth
    // throw new Error(error);
  }

  const updateRememberMe = (_: string, state: TInputState) => {
    setRememberMe((prevState) => ({ ...prevState, ...state }));
  };

  useEffect(() => {
    setRememberMe({ value: !!emailField?.defaultValue });
  }, [emailField, setRememberMe]);

  const inputGroups = createBasicFormConfig('login', display ?? [], {
    submit: { textKeyPath: 'button.login', onClick: submitForm },
  });

  useSubmitAutoLogin(submitForm);

  return (
    <Container>
      {loading || isAutoLogin ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : (
        <Form inputGroups={inputGroups} />
      )}
      {!isAutoLogin && (
        <>
          <ExtraOptions>
            {!hideRememberMe && !!emailField && (
              <FormInput
                dataKey="rememberMe"
                label={tb('login.inputField.rememberMe.label')}
                state={rememberMe}
                updateErrorMessages={noop}
                updateState={updateRememberMe}
                variant={FormInputVariants.CHECKBOX}
              />
            )}
            {!hideForgotPassword && (
              <Link
                data-testid="forgotPasswordLink"
                onClick={onCancel}
                to={{
                  pathname: Path.ForgotPassword,
                  search: techSearchParams(location.search),
                }}
              >
                {forgotPasswordLink}
              </Link>
            )}
          </ExtraOptions>
          {!hideRegister && (
            <RegistrationSection>
              {registerText}
              <Link
                aria-label={registerLink}
                onClick={onCancel}
                state={{ from: location.pathname }}
                to={{
                  pathname: Path.Registration,
                  search: techSearchParams(location.search),
                }}
              >
                {registerLink}
              </Link>
            </RegistrationSection>
          )}
        </>
      )}
    </Container>
  );
};
LoginStandard.displayName = 'LoginStandard';

async function performLogin(
  values: TFormData,
  pageHeading: string,
  authCode?: string
) {
  try {
    const req: LoginRequest = {
      deviceInfo: getDeviceInfo(),
      values: values as unknown as LoginRequestValues,
    };

    if (authCode) {
      req.secondScreen = {
        code: authCode,
      };
    }

    const resp = await userService.login(req);

    if (!authCode) {
      // When log in was executed with a second screen auth context then
      // we don't actually change anything regarding the current auth state
      await authActions.login(
        resp.data._meta.auth,
        ScreenType.Login,
        pageHeading
      );
      showPersonalGreeting(resp.data._meta.auth);
    }

    const secondScreenRedirectUrl = resp.data._meta.secondScreen?.redirectUrl;
    authActions.setSecondScreenRedirectUrl(secondScreenRedirectUrl);

    return resp.data;
  } catch (err) {
    if (!(err instanceof ApiError)) {
      throw err;
    }

    if (err.statusCode >= 500) {
      return { _meta: { type: CoreResponseType.ERROR } };
    }

    return err.details.content;
  }
}
